import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  createChatNote,
  updateChatNote,
  deleteChatNote,
  getWhatsAppErrorMessage,
} from "../../../services/whatsappApi";
import { formatRelativeTime } from "../../../utils/whatsappFormat";

const NOTE_MAX = 5000;

function StarRating({ value, onChange, disabled }) {
  const score = Number(value) || 0;
  return (
    <div className="wa-crm-stars" role="group" aria-label="Rate this chat note">
      {Array.from({ length: 10 }, (_, i) => {
        const starValue = i + 1;
        const filled = score >= starValue;
        return (
          <button
            key={starValue}
            type="button"
            className={`wa-crm-star ${filled ? "filled" : ""}`}
            onClick={() => onChange(filled && score === starValue ? 0 : starValue)}
            disabled={disabled}
            aria-label={`Rate ${starValue} of 10`}
            title={`${starValue}/10`}
          >
            <Icon
              icon={filled ? "solar:star-bold" : "solar:star-line-duotone"}
              width={22}
            />
          </button>
        );
      })}
    </div>
  );
}

function sortNotesNewestFirst(notes) {
  return [...(notes || [])].sort((a, b) => {
    const ta = new Date(a?.createdAt || 0).getTime();
    const tb = new Date(b?.createdAt || 0).getTime();
    return tb - ta;
  });
}

export default function ChatNotesPanel({ chatId, chatNotes, onNotesChange }) {
  const notes = useMemo(() => sortNotesNewestFirst(chatNotes), [chatNotes]);

  const [noteText, setNoteText] = useState("");
  const [rating, setRating] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const resetForm = () => {
    setNoteText("");
    setRating(0);
    setEditingId(null);
  };

  const applyNotesFromResponse = (res) => {
    if (Array.isArray(res?.chatNotes)) {
      onNotesChange(res.chatNotes);
      return;
    }
    if (Array.isArray(res?.data?.chatNotes)) {
      onNotesChange(res.data.chatNotes);
      return;
    }
    if (Array.isArray(res?.data) && res.data[0]?.note != null) {
      onNotesChange(res.data);
      return;
    }
    onNotesChange(null);
  };

  const handleSave = async () => {
    const text = noteText.trim();
    if (!chatId) return;
    if (!text) {
      toast.error("Note cannot be empty");
      return;
    }
    if (text.length > NOTE_MAX) {
      toast.error(`Note max ${NOTE_MAX} characters`);
      return;
    }

    setSaving(true);
    try {
      const payload = { note: text, rating: Number(rating) || 0 };
      let res;
      if (editingId) {
        res = await updateChatNote(chatId, editingId, payload);
      } else {
        res = await createChatNote(chatId, payload);
      }
      if (res?.success === false) {
        throw new Error(res.message || "Failed to save note");
      }
      toast.success(editingId ? "Note updated" : "Note saved");
      resetForm();
      applyNotesFromResponse(res);
    } catch (err) {
      toast.error(getWhatsAppErrorMessage(err, "Failed to save note"));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setNoteText(note.note || "");
    setRating(Number(note.rating) || 0);
  };

  const handleDelete = async (noteId) => {
    if (!chatId || !noteId) return;
    try {
      const res = await deleteChatNote(chatId, noteId);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to delete note");
      }
      toast.success("Note deleted");
      if (editingId === noteId) resetForm();
      applyNotesFromResponse(res);
    } catch (err) {
      toast.error(getWhatsAppErrorMessage(err, "Failed to delete note"));
    }
  };

  const handleDownloadNotes = () => {
    if (!notes.length) {
      toast.error("No notes to download");
      return;
    }
    const body = notes
      .map(
        (n) =>
          `[${n.createdAt || ""}] ${n.createdBy || ""} · ${n.rating ?? 0}/10\n${n.note || ""}\n`,
      )
      .join("\n---\n\n");
    const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chat-notes-${chatId || "export"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="wa-crm-section wa-crm-notes">
      <div className="wa-crm-section-head">
        <div className="wa-crm-section-title">
          <Icon icon="solar:chat-round-line-duotone" width={20} />
          <span>Chat note</span>
          <span className="wa-crm-count-badge">{notes.length}</span>
        </div>
        <div className="wa-crm-section-actions">
          <button
            type="button"
            className="wa-crm-icon-circle"
            title="Download notes"
            aria-label="Download notes"
            onClick={handleDownloadNotes}
          >
            <Icon icon="solar:download-minimalistic-line-duotone" width={18} />
          </button>
          <button
            type="button"
            className="wa-crm-save-btn"
            onClick={handleSave}
            disabled={saving || !noteText.trim()}
          >
            <Icon icon="solar:diskette-line-duotone" width={16} />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="wa-crm-rate-label">
        Rate this chat note:{" "}
        <strong>
          {Number(rating) || 0}/10
        </strong>
      </div>
      <StarRating value={rating} onChange={setRating} disabled={saving} />

      <textarea
        className="wa-crm-note-input"
        rows={4}
        maxLength={NOTE_MAX}
        placeholder="Add your notes here..."
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        disabled={saving}
      />

      {editingId ? (
        <button
          type="button"
          className="wa-text-action mt-1"
          onClick={resetForm}
          disabled={saving}
        >
          Cancel edit
        </button>
      ) : null}

      {notes.length > 0 ? (
        <ul className="wa-crm-notes-list">
          {notes.map((note) => (
            <li key={note.id} className="wa-crm-note-item">
              <div className="wa-crm-note-item-top">
                <span className="wa-note-rating">★ {note.rating ?? 0}/10</span>
                <span className="wa-note-meta">
                  {note.createdBy || "—"} · {formatRelativeTime(note.createdAt)}
                </span>
                <div className="wa-note-item-actions">
                  <button
                    type="button"
                    className="whatsapp-icon-btn"
                    title="Edit"
                    aria-label="Edit note"
                    onClick={() => startEdit(note)}
                  >
                    <Icon icon="solar:pen-line-duotone" width={16} />
                  </button>
                  <button
                    type="button"
                    className="whatsapp-icon-btn text-danger"
                    title="Delete"
                    aria-label="Delete note"
                    onClick={() => handleDelete(note.id)}
                  >
                    <Icon icon="solar:trash-bin-trash-line-duotone" width={16} />
                  </button>
                </div>
              </div>
              <p className="wa-note-text">{note.note}</p>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
